import { CalendarClock } from 'lucide-react'
import { SectionLabel } from '@/frontend/features/Home/SectionLabel'
import { PanelEmptyState } from '@/frontend/components/EmptyState'
import { nextRunLabelFromMs, scheduleSummaryLabel } from '@/frontend/features/HomeWork/format'
import type { CronData, CronJob } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

interface Props {
  crons?: CronData | null
  // Instância não-pronta: os crons nem são buscados (o server não manda a
  // prop) — o painel explica em vez de esperar um skeleton eterno.
  instanceNotReady?: boolean
}

const AGENDA_LIMIT = 6

/**
 * "Agendado" (Iteração 3, Onda B): os crons DESTE agente com próxima execução
 * — a mesma fonte real da aba Rotinas (cron.list → state.nextRunAtMs),
 * deferida na landing. Linhas quando (mono) + rotina + schedule, no padrão do
 * "Próximas execuções" da home. Agenda = futuro real do scheduler; histórico
 * fabricado não entra (a extrapolação rotinasExecutionProjection foi aposentada
 * na Onda C — o histórico agora é real, via fleet_sessions channel=cron).
 */
export function WorkerAgendaPanel({ crons = null, instanceNotReady = false }: Props) {
  const upcoming = (crons?.jobs ?? [])
    .filter((job: CronJob) => job.enabled && job.state?.nextRunAtMs != null)
    .sort((a, b) => (a.state.nextRunAtMs ?? 0) - (b.state.nextRunAtMs ?? 0))
    .slice(0, AGENDA_LIMIT)

  return (
    <section aria-label="Agendado" className="space-y-2" data-testid="worker-agenda-panel">
      <SectionLabel>Agendado</SectionLabel>

      {instanceNotReady ? (
        <p className="text-sm text-muted-foreground">
          A agenda abre quando a instância estiver pronta.
        </p>
      ) : crons?.error ? (
        <p className="text-sm text-muted-foreground">
          Não foi possível carregar a agenda agora.
        </p>
      ) : upcoming.length === 0 ? (
        <PanelEmptyState icon={CalendarClock} title="Nada agendado" description="Quando houver execução marcada, ela aparece aqui." />
      ) : (
        <ul className="overflow-hidden rounded-xl border border-border bg-card">
          {upcoming.map((job) => (
            <li
              key={job.id}
              className="flex items-center gap-3 border-b border-border px-4 py-[11px] last:border-b-0"
            >
              <span className="w-[92px] flex-none font-mono text-[11px] text-muted-foreground">
                {nextRunLabelFromMs(job.state.nextRunAtMs)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-medium text-foreground">{job.name}</p>
                <p className="truncate text-[11px] text-faint-foreground">{scheduleSummaryLabel(job.schedule)}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** Skeleton com a forma da lista (3 linhas) enquanto os crons chegam. */
export function WorkerAgendaPanelSkeleton() {
  return (
    <section aria-label="Agendado" className="space-y-2">
      <SectionLabel>Agendado</SectionLabel>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3 border-b border-border px-4 py-[11px] last:border-b-0">
            <div className="h-3 w-[72px] flex-none animate-pulse rounded bg-muted" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="h-3 w-2/5 animate-pulse rounded bg-muted" />
              <div className="h-2.5 w-1/4 animate-pulse rounded bg-muted" />
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
